import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { BASE_URL } from "../../utils/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

const USERS_BASE_URL = import.meta.env.VITE_USERS_BASE_URL;

const SubReplyForm = ({ replyId, email, onSubmit, onCancel, nestingLevel, clearMessage }) => {
    const username = email ? email.split("@")[0] : 'Unknown user';
    const [content, setContent] = useState(`@${username} `);

    const handleImageUploadLocal = (file) => {
        if (!file || file.size > 5 * 1024 * 1024) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setContent((prev) => `${prev}<img src='${reader.result}' alt='Uploaded image' style='max-width:100%;border-radius:8px;margin-top:1rem;'/>`);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = () => {
        onSubmit(replyId, content);
    };

    return (
        <Card className="mt-4" style={{ marginLeft: `${nestingLevel * 2}rem` }}>
            <CardHeader>
                <CardTitle className="text-lg">Replying to @{username}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <RichTextEditor
                        content={content}
                        onChange={(value) => {
                            setContent(value);
                            clearMessage();
                        }}
                        placeholder="Write a reply... (use @username to tag users)"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={`image-upload-${replyId}`}>Upload Image</Label>
                    <Input
                        id={`image-upload-${replyId}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                handleImageUploadLocal(file);
                            }
                        }}
                        className="cursor-pointer"
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                        onCancel();
                        setContent(`@${username} `);
                    }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit}>
                        Reply
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const QuestionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [question, setQuestion] = useState(null);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [replyContent, setReplyContent] = useState("");
    const [activeReplyId, setActiveReplyId] = useState(null);
    const [activeReplyEmail, setActiveReplyEmail] = useState("");
    const [showReplies, setShowReplies] = useState({});
    const [allRepliesCollapsed, setAllRepliesCollapsed] = useState(false);
    const [menuOpen, setMenuOpen] = useState(null);
    const [editingReply, setEditingReply] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    const fetchCurrentUser = async () => {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) return;
        try {
            const response = await fetch(`${USERS_BASE_URL}/profile/`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("user");
                    navigate("/login");
                }
                return;
            }
            const userData = await response.json();
            setCurrentUser(userData);
        } catch (err) {}
    };

    const fetchData = async () => {
        const accessToken = localStorage.getItem("accessToken");
        try {
            const response = await fetch(`${BASE_URL}/questions/${id}/`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
            });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("user");
                    navigate("/login");
                }
                return;
            }
            const questionData = await response.json();
            setQuestion(questionData);
        } catch (err) {
            navigate("/login");
        }
    };

    useEffect(() => {
        fetchCurrentUser();
        fetchData();
    }, [id, navigate]);

    const clearMessage = () => {
        if (message.text.includes("cannot be empty")) setMessage({ text: "", type: "" });
    };

    const handleImageUpload = (file, setContent) => {
        if (!file || file.size > 5 * 1024 * 1024) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setContent((prev) => `${prev}<img src='${reader.result}' alt='Uploaded image' style='max-width:100%;border-radius:8px;margin-top:1rem;'/>`);
        };
        reader.readAsDataURL(file);
    };

    const handlePostReply = async (parentReplyId = null, contentToPost) => {
        const content = contentToPost || replyContent;
        if (!content.trim()) {
            setMessage({ text: "Reply cannot be empty", type: "error" });
            return;
        }

        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            navigate("/login");
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/questions/${id}/reply/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    content,
                    parent_reply: parentReplyId,
                }),
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem("accessToken");
                    navigate("/login");
                }
                return;
            }

            const newReply = await response.json();
            setQuestion((prev) => ({
                ...prev,
                replies: [...(prev.replies || []), newReply],
            }));

            setReplyContent("");
            setActiveReplyId(null);
            setMessage({ text: "Reply posted successfully!", type: "success" });
        } catch (err) {
            setMessage({ text: "Failed to post reply", type: "error" });
        }
    };

    const handleLike = async (replyId) => {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            navigate("/login");
            return;
        }
        try {
            const response = await fetch(`${BASE_URL}/replies/${replyId}/like/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ action: "like" }),
            });
            if (response.ok) {
                const updatedReply = await response.json();
                setQuestion((prev) => ({
                    ...prev,
                    replies: updateReplyInTree(prev.replies, replyId, updatedReply),
                }));
            }
        } catch (err) {}
    };

    const updateReplyInTree = (replies, replyId, updatedReply) => {
        return replies.map((reply) => {
            if (reply.reply_id === replyId) {
                return { ...reply, like_count: updatedReply.like_count, has_liked: updatedReply.has_liked };
            }
            if (reply.sub_replies?.length) {
                return {
                    ...reply,
                    sub_replies: updateReplyInTree(reply.sub_replies, replyId, updatedReply),
                };
            }
            return reply;
        });
    };

    const handleEditReply = (reply) => {
        setEditingReply(reply);
        setReplyContent(reply.content);
        setActiveReplyId(null);
        setMenuOpen(null);
    };

    const handleUpdateReply = async (replyId, contentToUpdate) => {
        const plainText = contentToUpdate.replace(/<[^>]+>/g, "").trim();
        if (!plainText && !contentToUpdate.includes("<img")) {
            setMessage({ text: "Answer content cannot be empty.", type: "error" });
            return;
        }

        const accessToken = localStorage.getItem("accessToken");
        const formData = new FormData();
        formData.append("content", contentToUpdate);

        try {
            const response = await fetch(`${BASE_URL}/replies/${replyId}/`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("user");
                    navigate("/login");
                }
                return;
            }
            setEditingReply(null);
            setReplyContent("");
            fetchData();
        } catch (err) {}
    };

    const handleDeleteReply = async (replyId) => {
        const accessToken = localStorage.getItem("accessToken");
        try {
            const response = await fetch(`${BASE_URL}/replies/${replyId}/`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("user");
                    navigate("/login");
                }
                return;
            }
            fetchData();
        } catch (err) {}
        setMenuOpen(null);
    };

    const getRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return "just now";
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    const renderReplyContent = (content) => {
        return { __html: content };
    };

    const toggleAllReplies = (collapse) => {
        const newShowReplies = {};
        question.replies.forEach((reply) => {
            newShowReplies[reply.id] = !collapse;
        });
        setShowReplies(newShowReplies);
        setAllRepliesCollapsed(collapse);
    };

    const RenderReplies = ({ replies, nestingLevel = 0 }) => {
        if (!replies || replies.length === 0) return null;

            return (
            <div className="space-y-4">
                {replies.map((reply) => (
                    <Card key={reply.id} className="relative">
                        <CardContent className="pt-6">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                    <Avatar>
                                        <AvatarImage src={reply.user?.profile_image} />
                                        <AvatarFallback>
                                            {reply.user?.email?.charAt(0).toUpperCase() || 'U'}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <div className="font-medium">
                                            {reply.user?.email?.split('@')[0] || 'Unknown user'}
                                                    </div>
                                        <div className="text-sm text-muted-foreground">
                                            {getRelativeTime(reply.created_at)}
                                            </div>
                                    </div>
                                </div>

                                {currentUser && (
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleLike(reply.id)}
                                        >
                                            {reply.likes?.includes(currentUser.id) ? '❤️' : '🤍'} {reply.likes?.length || 0}
                                        </Button>
                                        {currentUser.id === reply.user?.id && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleEditReply(reply)}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div 
                                className="mt-4 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={renderReplyContent(reply.content)}
                            />

                            {reply.replies && reply.replies.length > 0 && (
                                <div className="mt-4">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowReplies(prev => ({
                                            ...prev,
                                            [reply.id]: !prev[reply.id]
                                        }))}
                                    >
                                        {showReplies[reply.id] ? 'Hide Replies' : `Show ${reply.replies.length} Replies`}
                                    </Button>
                                    </div>
                                )}

                            {showReplies[reply.id] && (
                                <div className="mt-4">
                                    <RenderReplies replies={reply.replies} nestingLevel={nestingLevel + 1} />
                                </div>
                            )}

                            {activeReplyId === reply.id ? (
                            <SubReplyForm
                                    replyId={reply.id}
                                    email={reply.user?.email}
                                onSubmit={handlePostReply}
                                    onCancel={() => setActiveReplyId(null)}
                                nestingLevel={nestingLevel}
                                clearMessage={clearMessage}
                            />
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => {
                                        setActiveReplyId(reply.id);
                                        setActiveReplyEmail(reply.user?.email);
                                    }}
                                >
                                    Reply
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ))}
                        </div>
        );
    };

    if (!question) return (
        <div className="container mx-auto py-6 flex justify-center">
            <Card className="w-full max-w-3xl overflow-x-auto">
                <p className="text-muted-foreground">Loading question...</p>
            </Card>
        </div>
    );

    return (
        <div className="container mx-auto py-6 flex justify-center">
            <Card className="container w-full max-w-3xl overflow-x-auto">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-2xl">{question.title}</CardTitle>
                        {currentUser && currentUser.id === question.user?.id && (
                            <Button variant="outline" onClick={() => navigate(`/dashboard/questions/${id}/edit`)}>
                                Edit Question
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex items-center gap-2">
                        <Avatar>
                            <AvatarImage src={question.user?.profile_image} />
                            <AvatarFallback>
                                {question.user?.email?.charAt(0).toUpperCase() || 'U'}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <div className="font-medium">
                                {question.user?.email?.split('@')[0] || 'Unknown user'}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {getRelativeTime(question.created_at)}
                            </div>
                        </div>
                    </div>

                    {question.image && (
                        <img 
                            src={question.image} 
                            alt={question.title} 
                            className="w-full max-h-96 object-contain rounded-md max-w-full h-auto"
                        />
                    )}

                    <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={renderReplyContent(question.details)}
                    />

                    <div className="flex flex-wrap gap-2">
                        {question.tags?.split(',').map((tag, index) => (
                            <Button
                                key={index}
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/dashboard/tags/${tag.trim()}`)}
                            >
                                #{tag.trim()}
                            </Button>
                        ))}
                </div>

                    <Separator />

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Replies</h3>
                            {question.replies?.length > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleAllReplies(!allRepliesCollapsed)}
                                >
                                    {allRepliesCollapsed ? 'Show All Replies' : 'Collapse All Replies'}
                                </Button>
                )}
            </div>

                {message.text && (
                            <div className={`p-4 rounded-md ${
                                message.type === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-green-100 text-green-800'
                            }`}>
                                {message.text}
                            </div>
                        )}

                        {currentUser ? (
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="space-y-4">
                                        <RichTextEditor
                                            content={replyContent}
                    onChange={(value) => {
                        setReplyContent(value);
                        clearMessage();
                    }}
                                            placeholder="Write your reply..."
                                        />

                                        <div className="space-y-2">
                                            <Label htmlFor="reply-image">Upload Image</Label>
                                            <Input
                                                id="reply-image"
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    handleImageUpload(file, setReplyContent);
                                }
                            }}
                                                className="cursor-pointer"
                        />
                    </div>

                                        <div className="flex justify-end">
                                            <Button onClick={() => handlePostReply()}>
                                                Post Reply
                                            </Button>
                </div>
            </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardContent className="pt-6">
                                    <p className="text-center text-muted-foreground">
                                        Please <Button variant="link" onClick={() => navigate('/login')}>login</Button> to reply
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        <RenderReplies replies={question.replies} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default QuestionDetail;